import LockOpenOutlined from '@mui/icons-material/LockOpenOutlined';
import { Stack, useMediaQuery, useTheme } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Door, DoorBatteryWarningLevel } from 'graphql/generated/graphqlTypes';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BatteryStatusCell from 'v2/components/BatteryStatusCell/BatteryStatusCell';
import { getOverviewDataGridProps } from 'v2/components/DataGrid/getOverviewDataGridProps';
import { renderHeaderWithId } from 'v2/components/DataGrid/renderHeaderWithId';
import DetailDataGridContainer from 'v2/components/DetailDataGridContainer/DetailDataGridContainer';
import EmptyStateOverview from 'v2/components/EmptyStateOverview/EmptyStateOverview';
import LockIconNameCell from 'v2/components/LockIconNameCell/LockIconNameCell';
import SearchAndFilters, { FilterChip, FilterSection } from 'v2/components/SearchAndFilters/SearchAndFilters';
import SectionPaper from 'v2/components/SectionPaper/SectionPaper';
import { DATAGRID_ROW_HEIGHT } from 'v2/constants';
import { FilterOfficeModeState } from 'v2/filters/devices/deviceFilterTypes';
import { useSearch } from 'v2/hooks/useSearch/useSearch';
import { getDevicePath } from 'v2/paths';
import { getOfficeModeDisplay } from 'v2/utils/scheduleDisplay';

const searchKeys = ['name'];

interface Props {
  doors: Door[];
  onEditDevices: () => void;
}

interface DeviceFilterState {
  batteryUnknown: boolean;
  batteryEmpty: boolean;
  batteryCritical: boolean;
  batteryLow: boolean;
  batteryFull: boolean;
}

const batteryFilterDefault: DeviceFilterState = {
  batteryUnknown: false,
  batteryEmpty: false,
  batteryCritical: false,
  batteryLow: false,
  batteryFull: false
};

const DevicesTab: React.FC<Props> = ({ doors, onEditDevices }) => {
  const { translation } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [filterStr, setFilterStr] = useState('');
  const [batteryFilter, setBatteryFilter] = useState<DeviceFilterState>(batteryFilterDefault);
  const [officeModeFilter, setOfficeModeFilter] = useState<FilterOfficeModeState>(null);

  const anyBatteryEnabled =
    batteryFilter.batteryUnknown ||
    batteryFilter.batteryEmpty ||
    batteryFilter.batteryCritical ||
    batteryFilter.batteryLow ||
    batteryFilter.batteryFull;

  let filteredDoors = useSearch({ data: doors, search: filterStr, keys: searchKeys });

  if (anyBatteryEnabled) {
    filteredDoors = filteredDoors.filter(
      (door) =>
        (batteryFilter.batteryUnknown && door.batteryWarningLevel === DoorBatteryWarningLevel.BatteryUnknown) ||
        (batteryFilter.batteryFull && door.batteryWarningLevel === DoorBatteryWarningLevel.BatteryDefault) ||
        (batteryFilter.batteryLow && door.batteryWarningLevel === DoorBatteryWarningLevel.BatteryLow) ||
        (batteryFilter.batteryCritical && door.batteryWarningLevel === DoorBatteryWarningLevel.BatteryCritical) ||
        (batteryFilter.batteryEmpty &&
          (door.batteryWarningLevel === DoorBatteryWarningLevel.BatteryEmpty ||
            door.batteryWarningLevel === DoorBatteryWarningLevel.Powered))
    );
  }

  if (officeModeFilter === FilterOfficeModeState.HasOfficeMode) {
    filteredDoors = filteredDoors.filter((door) => {
      const schedule = door.officeMode;
      return schedule?.weekDays && schedule.weekDays.length > 0;
    });
  } else if (officeModeFilter === FilterOfficeModeState.NoOfficeMode) {
    filteredDoors = filteredDoors.filter((door) => {
      const schedule = door.officeMode;
      return !schedule?.weekDays || schedule.weekDays.length === 0;
    });
  }

  const toggleBattery = (key: keyof DeviceFilterState) => {
    setBatteryFilter((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const sections: FilterSection[] = [
    {
      id: 'battery_status',
      header: translation.battery_status,
      type: 'checkbox',
      isClearDisabled: !anyBatteryEnabled,
      onClear: () => setBatteryFilter(batteryFilterDefault),
      items: [
        {
          key: 'batteryUnknown',
          label: translation.unknown,
          checked: batteryFilter.batteryUnknown,
          onChange: () => toggleBattery('batteryUnknown')
        },
        {
          key: 'batteryEmpty',
          label: translation.empty,
          checked: batteryFilter.batteryEmpty,
          onChange: () => toggleBattery('batteryEmpty')
        },
        {
          key: 'batteryCritical',
          label: translation.critical,
          checked: batteryFilter.batteryCritical,
          onChange: () => toggleBattery('batteryCritical')
        },
        {
          key: 'batteryLow',
          label: translation.low,
          checked: batteryFilter.batteryLow,
          onChange: () => toggleBattery('batteryLow')
        },
        {
          key: 'batteryFull',
          label: translation.full,
          checked: batteryFilter.batteryFull,
          onChange: () => toggleBattery('batteryFull')
        }
      ]
    },
    {
      id: 'office_mode',
      header: translation.office_mode,
      type: 'radio',
      isClearDisabled: officeModeFilter === null,
      onClear: () => setOfficeModeFilter(null),
      items: [
        {
          key: FilterOfficeModeState.HasOfficeMode,
          label: translation.has_office_mode,
          checked: officeModeFilter === FilterOfficeModeState.HasOfficeMode,
          onChange: () => setOfficeModeFilter(FilterOfficeModeState.HasOfficeMode)
        },
        {
          key: FilterOfficeModeState.NoOfficeMode,
          label: translation.no_office_mode,
          checked: officeModeFilter === FilterOfficeModeState.NoOfficeMode,
          onChange: () => setOfficeModeFilter(FilterOfficeModeState.NoOfficeMode)
        }
      ]
    }
  ];

  const chips: FilterChip[] = [
    ...(batteryFilter.batteryUnknown
      ? [{ label: translation.battery_state_unknown, onDelete: () => toggleBattery('batteryUnknown') }]
      : []),
    ...(batteryFilter.batteryEmpty
      ? [{ label: translation.battery_empty, onDelete: () => toggleBattery('batteryEmpty') }]
      : []),
    ...(batteryFilter.batteryCritical
      ? [{ label: translation.battery_critical, onDelete: () => toggleBattery('batteryCritical') }]
      : []),
    ...(batteryFilter.batteryLow
      ? [{ label: translation.battery_low, onDelete: () => toggleBattery('batteryLow') }]
      : []),
    ...(batteryFilter.batteryFull
      ? [{ label: translation.battery_full, onDelete: () => toggleBattery('batteryFull') }]
      : []),
    ...(officeModeFilter === FilterOfficeModeState.HasOfficeMode
      ? [{ label: translation.has_office_mode, onDelete: () => setOfficeModeFilter(null) }]
      : []),
    ...(officeModeFilter === FilterOfficeModeState.NoOfficeMode
      ? [{ label: translation.no_office_mode, onDelete: () => setOfficeModeFilter(null) }]
      : [])
  ];

  const headerWithId = renderHeaderWithId();

  const columns: GridColDef<Door>[] = [
    {
      field: 'name',
      headerName: translation.name,
      flex: 1,
      sortable: true,
      renderHeader: headerWithId,
      renderCell: (params) => <LockIconNameCell door={params.row} />
    },
    {
      field: 'officeMode',
      headerName: translation.office_mode,
      flex: 1,
      sortable: true,
      renderHeader: headerWithId,
      valueGetter: (_, row) => getOfficeModeDisplay(row.officeMode),
      renderCell: (params) => <span>{params.value}</span>
    },
    {
      field: 'hardwareIdentifier',
      headerName: translation.serial_number,
      flex: 1,
      sortable: true,
      renderHeader: headerWithId,
      valueGetter: (_, row) => row.hardwareIdentifier,
      renderCell: (params) => <span>{params.value}</span>
    },
    // TODO: bring back when available in the API
    // {
    //   field: 'firmware',
    //   headerName: translation.firmware,
    //   flex: 1,
    //   sortable: false,
    //   renderCell: () => 'Coming soon'
    // },
    {
      field: 'batteryWarningLevel',
      headerName: translation.status,
      flex: 1,
      sortable: true,
      renderHeader: headerWithId,
      renderCell: (params) => <BatteryStatusCell level={params.row.batteryWarningLevel} />
    }
  ];

  const isGroupEmpty = doors.length === 0;

  const renderContent = () => {
    if (isGroupEmpty) {
      return (
        <SectionPaper>
          <EmptyStateOverview
            icon={LockOpenOutlined}
            title={translation.no_devices_in_group}
            content={translation.lets_get_started_by_assigning_your_first_device}
            button={{ label: translation.add_device, onClick: onEditDevices }}
            sx={{ height: 'auto', pt: 8, pb: 4 }}
          />
        </SectionPaper>
      );
    }

    if (filteredDoors.length === 0) {
      return (
        <DetailDataGridContainer>
          <EmptyStateOverview
            icon={LockOpenOutlined}
            title={translation.no_results}
            content={translation.no_results_search_and_filters_description}
            sx={{ height: 'auto', pt: 8, pb: 4 }}
          />
        </DetailDataGridContainer>
      );
    }

    return (
      <DetailDataGridContainer>
        <DataGrid
          {...getOverviewDataGridProps({ rowCount: filteredDoors.length })}
          className="background-level2"
          rows={filteredDoors}
          columns={columns}
          columnVisibilityModel={{
            officeMode: isDesktop,
            hardwareIdentifier: isDesktop
          }}
          rowHeight={DATAGRID_ROW_HEIGHT}
          onRowClick={(params) => navigate(getDevicePath(params.row.id))}
        />
      </DetailDataGridContainer>
    );
  };

  return (
    <Stack spacing={2}>
      <SearchAndFilters
        filterStr={filterStr}
        onFilterStrChange={setFilterStr}
        sections={sections}
        chips={chips}
        countCaption={`${filteredDoors.length} ${translation.of} ${doors.length} ${translation.devices}`}
        actionButton={{
          id: 'edit-devices-button',
          label: isGroupEmpty ? translation.add_device : translation.edit_devices,
          onClick: onEditDevices
        }}
      />
      {renderContent()}
    </Stack>
  );
};

export default DevicesTab;
